import { FEATURE } from '@/core/features/featureCodes';
import { LAUNCHER_ERP_MODULES, moduleAdminPath } from '@/config/moduleDefaults';
import { withModuleScopedMasterRoutes } from '@/config/moduleSharedPaths';
import {
  moduleApprovalsBuilderPath,
  moduleApprovalsMatrixPath,
  moduleApprovalsPath,
  moduleExecutiveWorkPath,
  moduleNotificationsPath,
  moduleWorkPath,
} from '@/config/moduleWorkPaths';
import { access, type RouteAccessRule } from '@/config/routeAccessUtils';

/** Module-scoped administration routes (sidebar + route guards). */
function moduleAdminRouteAccess(): Record<string, RouteAccessRule> {
  const routes: Record<string, RouteAccessRule> = {};
  for (const mod of LAUNCHER_ERP_MODULES) {
    routes[moduleAdminPath(mod, 'administration')] = access(['ORG_MANAGE', 'ORG_VIEW']);
    routes[moduleAdminPath(mod, 'profile')] = access(null);
    routes[moduleAdminPath(mod, 'organization')] = access(['ORG_VIEW', 'ORG_MANAGE']);
    routes[moduleAdminPath(mod, 'companies')] = access(['COMPANY_VIEW', 'ORG_MANAGE'], 'COMPANY_MANAGE');
    routes[moduleAdminPath(mod, 'users')] = access(
      ['EMPLOYEE_VIEW', 'ORG_MANAGE'],
      'EMPLOYEE_MANAGE',
      FEATURE.EMPLOYEE_BASIC,
    );
    routes[moduleAdminPath(mod, 'roles')] = {
      view: 'ROLE_VIEW',
      create: 'ROLE_MANAGE',
      edit: 'ROLE_MANAGE',
      delete: 'ROLE_MANAGE',
      feature: FEATURE.EMPLOYEE_BASIC,
    };
    routes[moduleAdminPath(mod, 'subscription')] = access(['SUBSCRIPTION_MANAGE', 'ORG_MANAGE']);
    routes[moduleAdminPath(mod, 'settings')] = access(null);
  }
  return routes;
}

function moduleWorkRouteAccess(): Record<string, RouteAccessRule> {
  const routes: Record<string, RouteAccessRule> = {};
  for (const mod of LAUNCHER_ERP_MODULES) {
    routes[moduleWorkPath(mod)] = access('DASHBOARD_VIEW');
    routes[moduleExecutiveWorkPath(mod)] = access('DASHBOARD_VIEW');
    routes[moduleApprovalsPath(mod)] = access(
      ['APPROVAL_VIEW', 'APPROVAL_ACT', 'HR_VIEW', 'HR_APPROVAL_MANAGE'],
      'APPROVAL_ACT',
    );
    routes[moduleApprovalsBuilderPath(mod)] = access(
      ['APPROVAL_MANAGE', 'HR_APPROVAL_MANAGE'],
      'APPROVAL_MANAGE',
    );
    routes[moduleApprovalsMatrixPath(mod)] = access(
      ['APPROVAL_MATRIX_VIEW', 'APPROVAL_MANAGE', 'HR_APPROVAL_MANAGE'],
      'APPROVAL_MATRIX_VIEW',
    );
    routes[moduleNotificationsPath(mod)] = {
      view: null,
      create: 'NOTIFICATION_MANAGE',
      edit: 'NOTIFICATION_MANAGE',
      delete: 'NOTIFICATION_MANAGE',
    };
  }
  return routes;
}

/**
 * Single source of truth for org route access:
 * - view: page + nav visibility
 * - create/edit/delete: action buttons (via usePageAccess / PageActionGate)
 * - feature: subscription gating
 */
const BASE_ROUTE_ACCESS: Record<string, RouteAccessRule> = {
  ...moduleAdminRouteAccess(),
  ...moduleWorkRouteAccess(),
  // Platform & POS
  '/pos/dashboard': access(['DASHBOARD_VIEW', 'BILLING_VIEW'], undefined, FEATURE.REPORTING),
  '/org/profile': access(['ORG_VIEW', 'ORG_MANAGE']),
  '/pos/stores': access('STORE_VIEW', 'STORE_MANAGE'),
  '/org/employees': access('EMPLOYEE_VIEW', 'EMPLOYEE_MANAGE', FEATURE.EMPLOYEE_BASIC),
  '/org/roles': {
    view: 'ROLE_VIEW',
    create: 'ROLE_MANAGE',
    edit: 'ROLE_MANAGE',
    delete: 'ROLE_MANAGE',
    feature: FEATURE.EMPLOYEE_BASIC,
  },
  '/pos/products': access('PRODUCT_VIEW', 'PRODUCT_MANAGE', FEATURE.POS_BILLING),
  '/pos/categories': access('PRODUCT_VIEW', 'PRODUCT_MANAGE', FEATURE.POS_BILLING),
  '/pos/brands': access('PRODUCT_VIEW', 'PRODUCT_MANAGE', FEATURE.POS_BILLING),
  '/pos/units': access('PRODUCT_VIEW', 'PRODUCT_MANAGE', FEATURE.POS_BILLING),
  '/org/taxes': access('PRODUCT_VIEW', 'PRODUCT_MANAGE'),
  '/pos/tax-rules': access('PRODUCT_VIEW', 'PRODUCT_MANAGE', FEATURE.POS_BILLING),
  '/pos/store-products': access('PRODUCT_VIEW', 'PRODUCT_MANAGE', FEATURE.POS_BILLING),
  '/pos/invoices': access('BILLING_VIEW', 'BILLING_CREATE', FEATURE.POS_BILLING),
  '/pos/inventory': access('INVENTORY_VIEW', 'INVENTORY_MANAGE', FEATURE.INVENTORY),
  '/pos/inventory/ledger': access('INVENTORY_VIEW', 'INVENTORY_MANAGE', FEATURE.INVENTORY),
  '/pos/product-alerts': access('INVENTORY_VIEW', 'INVENTORY_MANAGE', FEATURE.INVENTORY),
  '/pos/suppliers': access('INVENTORY_VIEW', 'INVENTORY_MANAGE', FEATURE.PROCUREMENT),
  '/pos/customers': access('CUSTOMER_VIEW', 'CUSTOMER_MANAGE', FEATURE.CRM_BASIC),
  '/pos/discounts': access('PRODUCT_VIEW', 'PRODUCT_MANAGE', FEATURE.POS_BILLING),
  '/pos/reports': access('REPORT_VIEW', 'REPORT_VIEW', FEATURE.REPORTING),
  '/pos/terminal-reports': access('REPORT_VIEW', 'REPORT_VIEW', FEATURE.REPORTING),
  '/pos': access('BILLING_CREATE', 'BILLING_CREATE', FEATURE.POS_BILLING),
  '/pos/held': access('BILLING_VIEW', 'BILLING_CREATE', FEATURE.POS_BILLING),
  '/pos/shift': access('BILLING_CREATE', 'BILLING_CREATE', FEATURE.POS_SHIFT_MANAGEMENT),
  '/pos/returns': access('BILLING_RETURN', 'BILLING_RETURN', FEATURE.POS_RETURNS),
  '/pos/analytics': access(['BILLING_VIEW', 'REPORT_VIEW'], 'REPORT_VIEW', FEATURE.POS_ANALYTICS),
  '/pos/setup': access(['POS_ADMIN', 'STORE_MANAGE'], 'STORE_MANAGE', FEATURE.POS_BILLING),
  '/pos/shortcuts': access('BILLING_CREATE', 'BILLING_CREATE', FEATURE.POS_BILLING),
  '/work': access('DASHBOARD_VIEW'),
  '/work/approvals': access(['APPROVAL_VIEW', 'APPROVAL_ACT', 'HR_VIEW', 'HR_APPROVAL_MANAGE'], 'APPROVAL_ACT'),
  '/approvals/builder': access(['APPROVAL_MANAGE', 'HR_APPROVAL_MANAGE'], 'APPROVAL_MANAGE'),
  '/approvals/matrix': access(['APPROVAL_MATRIX_VIEW', 'APPROVAL_MANAGE', 'HR_APPROVAL_MANAGE'], 'APPROVAL_MATRIX_VIEW'),
  '/approvals/delegations': access(['APPROVAL_DELEGATE', 'APPROVAL_ACT'], 'APPROVAL_DELEGATE'),
  '/work/executive': access('DASHBOARD_VIEW'),
  '/notifications': { view: null, create: 'NOTIFICATION_MANAGE', edit: 'NOTIFICATION_MANAGE', delete: 'NOTIFICATION_MANAGE' },
  '/warehouse': access('WAREHOUSE_VIEW', 'WAREHOUSE_ADMIN', FEATURE.WAREHOUSE),
  '/warehouse/operations': access('WAREHOUSE_VIEW', 'WAREHOUSE_RECEIVE', FEATURE.WAREHOUSE),
  '/warehouse/mobile': access('WAREHOUSE_VIEW', 'WAREHOUSE_PICK', FEATURE.WAREHOUSE),
  '/warehouse/setup': access('WAREHOUSE_VIEW', 'WAREHOUSE_ADMIN', FEATURE.WAREHOUSE),
  '/warehouse/picks': access('WAREHOUSE_VIEW', 'WAREHOUSE_PICK', FEATURE.WAREHOUSE),
  '/warehouse/transfers': access('WAREHOUSE_VIEW', 'WAREHOUSE_TRANSFER', FEATURE.WAREHOUSE),
  '/warehouse/cycle-counts': access('WAREHOUSE_VIEW', 'WAREHOUSE_ADMIN', FEATURE.WAREHOUSE),
  '/warehouse/waves': access('WAREHOUSE_VIEW', 'WAREHOUSE_PICK', FEATURE.WAREHOUSE),
  '/warehouse/dock': access('WAREHOUSE_VIEW', 'WAREHOUSE_RECEIVE', FEATURE.WAREHOUSE),
  '/warehouse/cross-dock': access('WAREHOUSE_VIEW', 'WAREHOUSE_RECEIVE', FEATURE.WAREHOUSE),
  '/warehouse/labor': access('WAREHOUSE_VIEW', 'WAREHOUSE_ADMIN', FEATURE.WAREHOUSE),

  // Procurement
  '/procurement': access('PROCUREMENT_VIEW', 'PROCUREMENT_MANAGE', FEATURE.PROCUREMENT_BASIC),
  '/procurement/purchase-requests': access('PROCUREMENT_VIEW', ['PROCUREMENT_REQUEST_CREATE', 'PROCUREMENT_MANAGE'], FEATURE.PROCUREMENT_BASIC),
  '/procurement/rfqs': access('PROCUREMENT_VIEW', 'PROCUREMENT_RFQ_MANAGE', FEATURE.PROCUREMENT_RFQ),
  '/procurement/quotations': access('PROCUREMENT_VIEW', 'PROCUREMENT_RFQ_MANAGE', FEATURE.PROCUREMENT_RFQ),
  '/procurement/purchase-orders': access('PROCUREMENT_VIEW', ['PROCUREMENT_PO_CREATE', 'PROCUREMENT_MANAGE'], FEATURE.PROCUREMENT_BASIC),
  '/procurement/goods-receipts': access('PROCUREMENT_RECEIVE', 'PROCUREMENT_RECEIVE', FEATURE.PROCUREMENT_BASIC),
  '/procurement/vendor-bills': access('PROCUREMENT_VENDOR_BILL', 'PROCUREMENT_VENDOR_BILL', FEATURE.PROCUREMENT_BASIC),
  '/procurement/approvals': access(['PROCUREMENT_REQUEST_APPROVE', 'PROCUREMENT_PO_APPROVE'], ['PROCUREMENT_REQUEST_APPROVE', 'PROCUREMENT_PO_APPROVE'], FEATURE.PROCUREMENT_APPROVALS),
  '/procurement/reports': access('PROCUREMENT_ANALYTICS_VIEW', 'PROCUREMENT_MANAGE', FEATURE.PROCUREMENT_ADVANCED_ANALYTICS),
  '/procurement/setup': access('PROCUREMENT_VIEW', 'PROCUREMENT_MANAGE', FEATURE.PROCUREMENT_APPROVALS),
  '/procurement/contracts': access('PROCUREMENT_VIEW', 'PROCUREMENT_MANAGE', FEATURE.PROCUREMENT_CONTRACTS),
  '/procurement/catalogs': access('PROCUREMENT_VIEW', 'PROCUREMENT_MANAGE', FEATURE.PROCUREMENT_CATALOG),

  // CRM
  '/crm': access('CRM_VIEW', 'CRM_MANAGE', FEATURE.CRM_BASIC),
  '/crm/leads': access('CRM_VIEW', ['CRM_MANAGE', 'CRM_CONVERT'], FEATURE.CRM_BASIC),
  '/crm/contacts': access('CRM_VIEW', 'CRM_MANAGE', FEATURE.CRM_BASIC),
  '/crm/companies': access('CRM_VIEW', 'CRM_MANAGE', FEATURE.CRM_BASIC),
  '/crm/deals': access('CRM_VIEW', ['CRM_MANAGE', 'CRM_PIPELINE_MANAGE'], FEATURE.CRM_BASIC),
  '/crm/pipelines': access('CRM_VIEW', 'CRM_PIPELINE_MANAGE', FEATURE.CRM_PIPELINE),
  '/crm/followups': access('CRM_VIEW', 'CRM_MANAGE', FEATURE.CRM_BASIC),
  '/crm/tasks': access('CRM_VIEW', ['CRM_MANAGE', 'CRM_CONVERT'], FEATURE.CRM_BASIC),
  '/crm/campaigns': access('CRM_VIEW', 'CRM_CAMPAIGN_MANAGE', FEATURE.CRM_CAMPAIGNS),
  '/crm/analytics': access('CRM_ANALYTICS_VIEW', 'CRM_MANAGE', FEATURE.CRM_ANALYTICS),
  '/crm/reports': access('CRM_ANALYTICS_VIEW', 'CRM_MANAGE', FEATURE.CRM_ANALYTICS),
  '/crm/setup': access('CRM_VIEW', 'CRM_MANAGE', FEATURE.CRM_BASIC),

  // Lead generation
  '/lead-gen': access('LEAD_GEN_VIEW', 'LEAD_GEN_MANAGE'),
  '/lead-gen/forms': access('LEAD_GEN_VIEW', 'LEAD_GEN_MANAGE'),
  '/lead-gen/landing-pages': access('LEAD_GEN_VIEW', 'LEAD_GEN_MANAGE'),
  '/lead-gen/campaigns': access('LEAD_GEN_VIEW', ['LEAD_GEN_CAMPAIGN', 'LEAD_GEN_MANAGE']),
  '/lead-gen/visitor-tracking': access('LEAD_GEN_VIEW', 'LEAD_GEN_MANAGE'),
  '/lead-gen/scoring': access('LEAD_GEN_VIEW', 'LEAD_GEN_MANAGE'),
  '/lead-gen/routing': access('LEAD_GEN_VIEW', 'LEAD_GEN_MANAGE'),
  '/lead-gen/ab-tests': access('LEAD_GEN_VIEW', 'LEAD_GEN_MANAGE'),
  '/lead-gen/webhooks': access('LEAD_GEN_VIEW', 'LEAD_GEN_MANAGE'),
  '/lead-gen/enrichment': access('LEAD_GEN_VIEW', 'LEAD_GEN_MANAGE'),
  '/lead-gen/consent': access('LEAD_GEN_VIEW', 'LEAD_GEN_MANAGE'),
  '/lead-gen/reports': access('LEAD_GEN_ANALYTICS', 'LEAD_GEN_MANAGE'),
  '/lead-gen/sources': access('LEAD_GEN_VIEW', 'LEAD_GEN_MANAGE'),

  // Marketing
  '/marketing': access('MARKETING_VIEW', 'MARKETING_MANAGE', FEATURE.MARKETING_BASIC),
  '/marketing/campaigns': access('MARKETING_VIEW', ['MARKETING_CAMPAIGN', 'MARKETING_MANAGE'], FEATURE.MARKETING_EMAIL),
  '/marketing/segments': access('MARKETING_VIEW', 'MARKETING_MANAGE', FEATURE.MARKETING_BASIC),
  '/marketing/journeys': access('MARKETING_VIEW', ['MARKETING_AUTOMATION', 'MARKETING_MANAGE'], FEATURE.MARKETING_JOURNEY),
  '/marketing/templates': access('MARKETING_VIEW', 'MARKETING_MANAGE', FEATURE.MARKETING_EMAIL),
  '/marketing/lifecycle': access('MARKETING_VIEW', ['MARKETING_AUTOMATION', 'MARKETING_MANAGE'], FEATURE.MARKETING_AUTOMATION),
  '/marketing/reports': access('MARKETING_ANALYTICS', 'MARKETING_MANAGE', FEATURE.MARKETING_ATTRIBUTION),
  '/marketing/webhooks': access('MARKETING_VIEW', 'MARKETING_MANAGE', FEATURE.MARKETING_EMAIL),

  // Social
  '/social': access('SOCIAL_VIEW', 'SOCIAL_MANAGE', FEATURE.SOCIAL_BASIC),
  '/social/inbox': access(['SOCIAL_VIEW', 'SOCIAL_INBOX_REPLY'], 'SOCIAL_INBOX_REPLY', FEATURE.SOCIAL_INBOX),
  '/social/channels': access('SOCIAL_VIEW', 'SOCIAL_CHANNEL_MANAGE', FEATURE.SOCIAL_BASIC),
  '/social/publishing': access('SOCIAL_VIEW', 'SOCIAL_PUBLISH', FEATURE.SOCIAL_PUBLISHING),
  '/social/calendar': access('SOCIAL_VIEW', 'SOCIAL_PUBLISH', FEATURE.SOCIAL_CALENDAR),
  '/social/media': access('SOCIAL_VIEW', 'SOCIAL_PUBLISH', FEATURE.SOCIAL_PUBLISHING),
  '/social/analytics': access('SOCIAL_ANALYTICS_VIEW', 'SOCIAL_MANAGE', FEATURE.SOCIAL_ANALYTICS),
  '/social/automation': access('SOCIAL_VIEW', 'SOCIAL_MANAGE', FEATURE.SOCIAL_AUTOMATION),
  '/social/crm-leads': access('SOCIAL_VIEW', 'SOCIAL_MANAGE', FEATURE.SOCIAL_BASIC),
  '/social/teams': access('SOCIAL_VIEW', 'SOCIAL_MANAGE', FEATURE.SOCIAL_BASIC),
  '/social/comments': access('SOCIAL_VIEW', 'SOCIAL_INBOX_REPLY', FEATURE.SOCIAL_INBOX),
  '/social/connect': access('SOCIAL_VIEW', 'SOCIAL_CHANNEL_MANAGE', FEATURE.SOCIAL_BASIC),
  '/social/settings': access('SOCIAL_VIEW', 'SOCIAL_MANAGE', FEATURE.SOCIAL_BASIC),
  '/social/platforms': access('SOCIAL_VIEW', 'SOCIAL_CHANNEL_MANAGE', FEATURE.SOCIAL_BASIC),
  '/social/approvals': access('SOCIAL_VIEW', 'SOCIAL_PUBLISH', FEATURE.SOCIAL_APPROVALS),
  '/social/webhooks': access('SOCIAL_VIEW', 'SOCIAL_MANAGE', FEATURE.SOCIAL_BASIC),
  '/social/listening': access('SOCIAL_VIEW', 'SOCIAL_MANAGE', FEATURE.SOCIAL_LISTENING),
  '/social/campaigns': access('SOCIAL_VIEW', 'SOCIAL_CAMPAIGN_MANAGE', FEATURE.SOCIAL_CAMPAIGNS),
  '/social/reviews': access('SOCIAL_VIEW', 'SOCIAL_REVIEW_MANAGE', FEATURE.SOCIAL_REPUTATION),
  '/social/reports': access('SOCIAL_ANALYTICS_VIEW', 'SOCIAL_MANAGE', FEATURE.SOCIAL_ANALYTICS),
  '/social/setup': access('SOCIAL_VIEW', 'SOCIAL_MANAGE', FEATURE.SOCIAL_BASIC),

  // HRMS
  '/hr': access('HR_VIEW', 'HR_MANAGE', FEATURE.HR_BASIC),
  '/hr/employees': access('HR_VIEW', ['HR_EMPLOYEE_MANAGE', 'HR_MANAGE'], FEATURE.HR_BASIC),
  '/hr/org': access('HR_VIEW', 'HR_MANAGE', FEATURE.HR_BASIC),
  '/hr/attendance': access(
    ['HR_VIEW', 'HR_ATTENDANCE_MANAGE', 'HR_LEAVE_MANAGE', 'HR_MANAGE'],
    ['HR_ATTENDANCE_MANAGE', 'HR_LEAVE_MANAGE', 'HR_MANAGE'],
    FEATURE.HR_BASIC,
  ),
  '/hr/devices': access(['HR_VIEW', 'HR_ATTENDANCE_MANAGE'], 'HR_ATTENDANCE_MANAGE', FEATURE.HR_ATTENDANCE),
  '/hr/leaves': access(['HR_VIEW', 'HR_LEAVE_MANAGE', 'HR_MANAGE'], 'HR_LEAVE_MANAGE', FEATURE.HR_BASIC),
  '/hr/master-data': access(['HR_VIEW', 'HR_LEAVE_MANAGE', 'HR_LEAVE_POLICY_MANAGE', 'HR_MANAGE'], 'HR_MANAGE', FEATURE.HR_BASIC),
  '/hr/leave-policies': access(
    ['HR_VIEW', 'HR_LEAVE_POLICY_MANAGE', 'HR_LEAVE_MANAGE', 'HR_MANAGE'],
    ['HR_LEAVE_POLICY_MANAGE', 'HR_MANAGE'],
    FEATURE.HR_BASIC,
  ),
  '/hr/shifts': access(['HR_VIEW', 'HR_ATTENDANCE_MANAGE'], 'HR_ATTENDANCE_MANAGE', FEATURE.HR_ATTENDANCE),
  '/hr/overtime': access(['HR_VIEW', 'HR_ATTENDANCE_MANAGE'], 'HR_ATTENDANCE_MANAGE', FEATURE.HR_ATTENDANCE),
  '/hr/payroll': access(['HR_VIEW', 'HR_PAYROLL_MANAGE', 'HR_MANAGE', 'ORG_MANAGE'], 'HR_PAYROLL_MANAGE', FEATURE.HR_BASIC),
  '/hr/compensation': access(['HR_VIEW', 'HR_PAYROLL_MANAGE', 'HR_MANAGE', 'ORG_MANAGE'], 'HR_PAYROLL_MANAGE', FEATURE.HR_BASIC),
  '/hr/statutory': access(['HR_VIEW', 'HR_PAYROLL_MANAGE', 'HR_MANAGE', 'ORG_MANAGE'], 'HR_PAYROLL_MANAGE', FEATURE.HR_BASIC),
  '/hr/claims': access(['HR_VIEW', 'HR_CLAIM_MANAGE', 'HR_MANAGE', 'ORG_MANAGE'], 'HR_CLAIM_MANAGE', FEATURE.HR_BASIC),
  '/hr/travel': access(['HR_VIEW', 'HR_CLAIM_MANAGE', 'HR_MANAGE', 'ORG_MANAGE'], 'HR_CLAIM_MANAGE', FEATURE.HR_BASIC),
  '/hr/reports': access(['HR_ANALYTICS_VIEW', 'HR_VIEW'], 'HR_MANAGE', FEATURE.HR_BASIC),
  '/hr/setup': access('HR_VIEW', 'HR_MANAGE', FEATURE.HR_BASIC),
  '/hr/workflows': access('HR_VIEW', 'HR_MANAGE', FEATURE.HR_BASIC),
  '/hr/recruitment': access(['HR_VIEW', 'HR_RECRUITMENT_MANAGE', 'HR_MANAGE', 'ORG_MANAGE'], 'HR_RECRUITMENT_MANAGE', FEATURE.HR_RECRUITMENT),
  '/hr/onboarding': access('HR_VIEW', 'HR_MANAGE', FEATURE.HR_BASIC),
  '/hr/documents': access('HR_VIEW', 'HR_MANAGE', FEATURE.HR_BASIC),
  '/hr/succession': access('HR_VIEW', 'HR_MANAGE', FEATURE.HR_BASIC),
  '/hr/admin': access('HR_VIEW', 'HR_ADMIN', FEATURE.HR_BASIC),
  '/hr/training': access(['HR_VIEW', 'HR_TRAINING_MANAGE'], 'HR_TRAINING_MANAGE', FEATURE.HR_BASIC),
  '/hr/ess': access(null, null, FEATURE.HR_BASIC),
  '/hr/mss': access(['HR_VIEW', 'HR_APPROVAL_MANAGE'], 'HR_APPROVAL_MANAGE', FEATURE.HR_BASIC),
  '/hr/appraisal': access('HR_VIEW', 'HR_MANAGE', FEATURE.HR_APPRAISAL),
  '/hr/feedback360': access('HR_VIEW', 'HR_MANAGE', FEATURE.HR_APPRAISAL),
  '/hr/approvals': access(['HR_VIEW', 'HR_APPROVAL_MANAGE'], 'HR_APPROVAL_MANAGE', FEATURE.HR_BASIC),
  '/hr/holidays': access(['HR_VIEW', 'HR_LEAVE_MANAGE'], 'HR_MANAGE', FEATURE.HR_BASIC),
  '/hr/loans': access(['HR_VIEW', 'HR_PAYROLL_MANAGE', 'HR_MANAGE', 'ORG_MANAGE'], 'HR_PAYROLL_MANAGE', FEATURE.HR_BASIC),
  '/hr/asset-requests': access('HR_VIEW', 'HR_MANAGE', FEATURE.HR_BASIC),

  // Help desk
  '/desk': access('DESK_VIEW', 'DESK_WORKFLOW_MANAGE', FEATURE.DESK_BASIC),
  '/desk/tickets': access('DESK_VIEW', 'DESK_WORKFLOW_MANAGE', FEATURE.DESK_BASIC),
  '/desk/support-360': access('DESK_VIEW', 'DESK_PROJECT_MANAGE', FEATURE.DESK_CUSTOMER_360),
  '/desk/inbox': access('DESK_VIEW', 'DESK_WORKFLOW_MANAGE', FEATURE.DESK_BASIC),
  '/desk/livechat': access('DESK_VIEW', 'DESK_WORKFLOW_MANAGE', FEATURE.DESK_LIVE_CHAT),
  '/desk/projects': access('DESK_VIEW', 'DESK_PROJECT_MANAGE', FEATURE.DESK_PROJECTS),
  '/desk/boards': access('DESK_VIEW', 'DESK_PROJECT_MANAGE', FEATURE.DESK_PROJECTS),
  '/desk/knowledgebase': access('DESK_VIEW', 'DESK_KB_MANAGE', FEATURE.DESK_BASIC),
  '/desk/reports': access('DESK_ANALYTICS_VIEW', 'DESK_AUTOMATION_MANAGE', FEATURE.DESK_ANALYTICS),
  '/desk/automation': access('DESK_VIEW', 'DESK_AUTOMATION_MANAGE', FEATURE.DESK_AUTOMATION),
  '/desk/setup': access('DESK_VIEW', 'DESK_WORKFLOW_MANAGE', FEATURE.DESK_BASIC),
  '/desk/setup/workflows': access('DESK_VIEW', 'DESK_WORKFLOW_MANAGE', FEATURE.DESK_BASIC),
  '/desk/canned-responses': access('DESK_VIEW', 'DESK_WORKFLOW_MANAGE', FEATURE.DESK_BASIC),

  // Finance
  '/finance': access('FINANCE_VIEW', 'FINANCE_MANAGE', FEATURE.FINANCE_BASIC),
  '/finance/receivables': access('FINANCE_VIEW', 'FINANCE_INVOICE_MANAGE', FEATURE.FINANCE_BASIC),
  '/finance/payables': access('FINANCE_VIEW', 'FINANCE_AP_MANAGE', FEATURE.FINANCE_BASIC),
  '/finance/payables/ap': access('FINANCE_VIEW', 'FINANCE_AP_MANAGE', FEATURE.FINANCE_BASIC),
  '/finance/tax': access('FINANCE_VIEW', 'FINANCE_TAX_MANAGE', FEATURE.FINANCE_BASIC),
  '/finance/control': access('FINANCE_VIEW', 'FINANCE_MANAGE', FEATURE.FINANCE_BASIC),
  '/finance/setup/wizard': access('FINANCE_VIEW', 'FINANCE_SETTINGS_MANAGE', FEATURE.FINANCE_BASIC),
  '/finance/health': access('FINANCE_VIEW', 'FINANCE_MANAGE', FEATURE.FINANCE_BASIC),
  '/finance/close': access('FINANCE_VIEW', 'FINANCE_MANAGE', FEATURE.FINANCE_ACCOUNTING),
  '/finance/control/rules': access('FINANCE_VIEW', 'FINANCE_SETTINGS_MANAGE', FEATURE.FINANCE_ACCOUNTING),
  '/finance/treasury/forecast': access('FINANCE_VIEW', 'FINANCE_TREASURY_MANAGE', FEATURE.FINANCE_BASIC),
  '/finance/collections': access('FINANCE_VIEW', 'FINANCE_INVOICE_MANAGE', FEATURE.FINANCE_BASIC),
  '/finance/automation': access('FINANCE_VIEW', 'FINANCE_MANAGE', FEATURE.FINANCE_ACCOUNTING),
  '/finance/analytics': access('FINANCE_REPORT_VIEW', 'FINANCE_MANAGE', FEATURE.FINANCE_ADVANCED_REPORTS),
  '/finance/compliance': access('FINANCE_VIEW', 'FINANCE_TAX_MANAGE', FEATURE.FINANCE_BASIC),
  '/finance/audit': access('FINANCE_VIEW', 'FINANCE_MANAGE', FEATURE.FINANCE_ACCOUNTING),
  '/finance/documents': access('FINANCE_VIEW', 'FINANCE_AP_MANAGE', FEATURE.FINANCE_BASIC),
  '/finance/integrations': access('FINANCE_VIEW', 'FINANCE_MANAGE', FEATURE.FINANCE_BASIC),
  '/finance/accounting': access('FINANCE_VIEW', ['FINANCE_MANAGE', 'FINANCE_JOURNAL_MANAGE'], FEATURE.FINANCE_ACCOUNTING),
  '/finance/ledger': access('FINANCE_VIEW', 'FINANCE_JOURNAL_MANAGE', FEATURE.FINANCE_ACCOUNTING),
  '/finance/invoices': access('FINANCE_VIEW', 'FINANCE_INVOICE_MANAGE', FEATURE.FINANCE_BASIC),
  '/finance/payments': access('FINANCE_VIEW', 'FINANCE_PAYMENT_MANAGE', FEATURE.FINANCE_BASIC),
  '/finance/banking': access('FINANCE_VIEW', 'FINANCE_BANKING_MANAGE', FEATURE.FINANCE_BASIC),
  '/finance/taxes': access('FINANCE_VIEW', 'FINANCE_TAX_MANAGE', FEATURE.FINANCE_BASIC),
  '/finance/expenses': access('FINANCE_VIEW', 'FINANCE_EXPENSE_MANAGE', FEATURE.FINANCE_BASIC),
  '/finance/assets': access('FINANCE_VIEW', 'FINANCE_ASSET_MANAGE', FEATURE.FINANCE_ASSETS),
  '/finance/budgets': access('FINANCE_VIEW', 'FINANCE_MANAGE', FEATURE.FINANCE_BUDGETING),
  '/finance/treasury': access('FINANCE_VIEW', 'FINANCE_TREASURY_MANAGE', FEATURE.FINANCE_BASIC),
  '/finance/revenue': access('FINANCE_VIEW', 'FINANCE_MANAGE', FEATURE.FINANCE_ACCOUNTING),
  '/finance/workflows': access('FINANCE_VIEW', 'FINANCE_SETTINGS_MANAGE', FEATURE.FINANCE_ACCOUNTING),
  '/finance/workflows/designer': access('FINANCE_VIEW', 'FINANCE_SETTINGS_MANAGE', FEATURE.FINANCE_ACCOUNTING),
  '/finance/reports': access('FINANCE_REPORT_VIEW', 'FINANCE_MANAGE', FEATURE.FINANCE_ADVANCED_REPORTS),
  '/finance/reports/builder': access('FINANCE_REPORT_VIEW', 'FINANCE_MANAGE', FEATURE.FINANCE_ADVANCED_REPORTS),
  '/finance/setup': access('FINANCE_VIEW', 'FINANCE_SETTINGS_MANAGE', FEATURE.FINANCE_BASIC),

  // Projects
  '/projects': access('PROJECT_VIEW', 'PROJECT_MANAGE', FEATURE.PROJECTS_BASIC),
  '/projects/list': access('PROJECT_VIEW', 'PROJECT_MANAGE', FEATURE.PROJECTS_BASIC),
  '/projects/tasks': access('PROJECT_VIEW', 'PROJECT_TASK_MANAGE', FEATURE.PROJECTS_BASIC),
  '/projects/boards': access('PROJECT_VIEW', 'PROJECT_MANAGE', FEATURE.PROJECTS_BASIC),
  '/projects/sprints': access('PROJECT_VIEW', 'PROJECT_SPRINT_MANAGE', FEATURE.PROJECTS_SPRINTS),
  '/projects/timesheets': access('PROJECT_VIEW', 'PROJECT_TIMESHEET_MANAGE', FEATURE.PROJECTS_TIMESHEETS),
  '/projects/portfolio': access('PROJECT_VIEW', 'PROJECT_PORTFOLIO_MANAGE', FEATURE.PROJECTS_PORTFOLIO),
  '/projects/issues': access('PROJECT_VIEW', 'PROJECT_ISSUE_MANAGE', FEATURE.PROJECTS_BASIC),
  '/projects/gantt': access('PROJECT_VIEW', 'PROJECT_MANAGE', FEATURE.PROJECTS_GANTT),
  '/projects/resources': access('PROJECT_VIEW', 'PROJECT_MANAGE', FEATURE.PROJECTS_BASIC),
  '/projects/calendar': access('PROJECT_VIEW', 'PROJECT_MANAGE', FEATURE.PROJECTS_BASIC),
  '/projects/workload': access('PROJECT_ANALYTICS_VIEW', 'PROJECT_MANAGE', FEATURE.PROJECTS_ADVANCED_ANALYTICS),
  '/projects/reports': access('PROJECT_ANALYTICS_VIEW', 'PROJECT_MANAGE', FEATURE.PROJECTS_ADVANCED_ANALYTICS),
  '/projects/automation': access('PROJECT_VIEW', 'PROJECT_AUTOMATION_MANAGE', FEATURE.PROJECTS_AUTOMATION),
  '/projects/setup': access('PROJECT_VIEW', 'PROJECT_MANAGE', FEATURE.PROJECTS_BASIC),

  // Org admin
  '/org/subscription': access('SUBSCRIPTION_MANAGE', 'SUBSCRIPTION_MANAGE'),
  '/org/administration/billing': access('SUBSCRIPTION_MANAGE', 'SUBSCRIPTION_MANAGE'),
  '/org/settings': access('ORG_MANAGE', 'ORG_MANAGE'),
  '/org/administration': access('ORG_MANAGE', 'ORG_MANAGE'),
  '/org/workspace/settings': access(null, null),
  '/org/workspace/subscription': access(null, null),
};

/** Canonical routes for screen permission matrix (excludes module-scoped master mirrors). */
export const SCREEN_ROUTE_ACCESS = BASE_ROUTE_ACCESS;

export const ORG_ROUTE_ACCESS = withModuleScopedMasterRoutes(BASE_ROUTE_ACCESS);

/** Legacy flat permission map for route guards */
export const ORG_ROUTE_VIEW_PERMISSIONS: Record<string, string | string[] | null> = Object.fromEntries(
  Object.entries(ORG_ROUTE_ACCESS).map(([path, rule]) => [path, rule.view ?? null]),
);

/** Legacy flat feature map */
export const ORG_ROUTE_FEATURES: Record<string, string | null> = Object.fromEntries(
  Object.entries(ORG_ROUTE_ACCESS).map(([path, rule]) => [path, rule.feature ?? null]),
);
